import { envConfig } from 'common/config';

declare global {
  // eslint-disable-next-line
  interface Window {
    ym: any;
  }
}

let isInitialized = false;
let eventQueue: Array<{ type: 'event' | 'pageview'; data: any[] }> = [];

export const initYandexMetrika = (): void => {
  if (envConfig.NODE_ENV !== 'production' || isInitialized) {
    return;
  }

  const metrikaId = envConfig.YANDEX_METRIKA_ID;

  if (!metrikaId) {
    console.warn('Yandex Metrika ID is not configured');
    return;
  }

  // Create and inject the script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://mc.yandex.ru/metrika/tag.js';

  // Initialize Yandex Metrika following official implementation
  window.ym =
    window.ym ||
    function (...args: any[]) {
      (window.ym.a = window.ym.a || []).push(args);
    };
  window.ym.l = 1 * new Date().getTime();

  // Check if script already exists to prevent duplicate loading
  for (let j = 0; j < document.scripts.length; j++) {
    if (document.scripts[j].src.includes('https://mc.yandex.ru/metrika/tag.js')) {
      return;
    }
  }

  script.onload = () => {
    try {
      // Initialize the counter with official parameters
      window.ym(metrikaId, 'init', {
        ssr: true,
        webvisor: true,
        clickmap: true,
        ecommerce: 'dataLayer',
        accurateTrackBounce: true,
        trackLinks: true,
      });

      isInitialized = true;
      if (import.meta.env.DEV) {
        console.log('Yandex Metrika initialized successfully');
      }

      // Flush queued events
      flushEventQueue();
    } catch (error) {
      console.error('Error initializing Yandex Metrika:', error);
    }
  };

  script.onerror = () => {
    console.error('Failed to load Yandex Metrika script');
  };

  // Insert script using the official method
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  // Add noscript fallback (only if body is available)
  if (document.body) {
    const noscript = document.createElement('noscript');
    const div = document.createElement('div');
    const img = document.createElement('img');
    img.src = `https://mc.yandex.ru/watch/${metrikaId}`;
    img.style.position = 'absolute';
    img.style.left = '-9999px';
    img.alt = '';
    div.appendChild(img);
    noscript.appendChild(div);
    document.body.appendChild(noscript);
  }
};

// Flush queued events after initialization
const flushEventQueue = (): void => {
  const metrikaId = envConfig.YANDEX_METRIKA_ID;

  if (!window.ym || !metrikaId) {
    return;
  }

  eventQueue.forEach((queuedEvent) => {
    try {
      if (queuedEvent.type === 'event') {
        window.ym(metrikaId, 'reachGoal', ...queuedEvent.data);
      } else if (queuedEvent.type === 'pageview') {
        window.ym(metrikaId, 'hit', ...queuedEvent.data);
      }
    } catch (error) {
      console.error('Error flushing Yandex Metrika event:', error);
    }
  });

  // Clear the queue
  eventQueue = [];
};

// Helper function to track events
export const trackEvent = (eventName: string, params?: Record<string, any>): void => {
  if (envConfig.NODE_ENV !== 'production') {
    return;
  }

  const metrikaId = envConfig.YANDEX_METRIKA_ID;

  if (!metrikaId) {
    return;
  }

  // If not initialized yet, queue the event
  if (!isInitialized) {
    eventQueue.push({
      type: 'event',
      data: [eventName, params],
    });
    return;
  }

  // Send immediately if initialized
  if (window.ym) {
    window.ym(metrikaId, 'reachGoal', eventName, params);
  }
};

// Helper function to track page views
export const trackPageView = (url?: string): void => {
  if (envConfig.NODE_ENV !== 'production') {
    return;
  }

  const metrikaId = envConfig.YANDEX_METRIKA_ID;

  if (!metrikaId) {
    return;
  }

  const targetUrl = url || window.location.href;

  // If not initialized yet, queue the pageview
  if (!isInitialized) {
    eventQueue.push({
      type: 'pageview',
      data: [targetUrl],
    });
    return;
  }

  // Send immediately if initialized
  if (window.ym) {
    window.ym(metrikaId, 'hit', targetUrl);
  }
};
